import { Button } from "@/components/ui/button"
import modalStyles from "../modals.module.scss"
import styles from "./styles.module.scss"
import { Check, CheckCircle2 } from "lucide-react"
import { useAutoTranslation, useModalStore } from "@/entities"
import { DISCOVERED_TRANSLATION_KEYS } from "@/shared/constants"
import { Modal } from "@/shared/types"
import {
  NewCheckoutSessionDtoReqPeriod,
  stripeControllerNewCheckout,
  stripeControllerTrialEligibility,
} from "@/shared/api/generated"
import { useEffect, useMemo, useState } from "react"
import { cn } from "@/lib/utils"
import { SvgLogo } from "@/shared/ui/svg"
import {
  PRIVACY_POLICY_URL,
  RESTORE_PURCHASES_URL,
  TERMS_OF_SERVICE_URL,
} from "@/shared/constants/basic"

export const ListenUntilModal = ({
  initialPeriod = NewCheckoutSessionDtoReqPeriod.YEAR,
}: {
  initialPeriod?: NewCheckoutSessionDtoReqPeriod
}) => {
  const { t } = useAutoTranslation()
  const [selectedPeriod, setSelectedPeriod] =
    useState<NewCheckoutSessionDtoReqPeriod>(initialPeriod)
  const [isLoading, setIsLoading] = useState(false)
  // The free trial is only for a first subscription (one per account and
  // card): promise it only when the server says this checkout gets it.
  const [trialDays, setTrialDays] = useState(0)

  useEffect(() => {
    let cancelled = false
    stripeControllerTrialEligibility()
      .then(({ trialDays }) => {
        if (!cancelled) setTrialDays(trialDays)
      })
      .catch(() => {
        // Unknown: neutral copy rather than a promise we may not keep.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleClose = () => {
    useModalStore.getState().closeModal(Modal.LISTEN_UNTIL)
  }

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()
      }
    }

    window.addEventListener("keydown", handleKeyPress)
    return () => {
      window.removeEventListener("keydown", handleKeyPress)
    }
  }, [])

  const features = useMemo(
    () => [
      {
        title: DISCOVERED_TRANSLATION_KEYS.UNLIMITED_AUDIO_CONVERSIONS,
        description:
          DISCOVERED_TRANSLATION_KEYS.TRANSFORM_ANY_DOCUMENT_ARTICLE_OR_WEB_PAGE_INTO_ENGAGING_HIGH_QUALITY_AUDIO,
      },
      {
        title: DISCOVERED_TRANSLATION_KEYS.OFFLINE_LISTENING_ANYWHERE,
        description:
          DISCOVERED_TRANSLATION_KEYS.BOOST_PRODUCTIVITY_BY_READING_WHILE_YOU_DRIVE_WORK_OR_MULTI_TASK,
      },
      {
        title: DISCOVERED_TRANSLATION_KEYS.DOWNLOAD_SHARE_INSTANTLY,
        description:
          DISCOVERED_TRANSLATION_KEYS.GENERATE_AUDIO_FILES_AND_SHARE_THEM_WITH_FRIENDS_FAMILY_OR_COLLEAGUES,
      },
      {
        title: DISCOVERED_TRANSLATION_KEYS.PERSONALIZE_YOUR_EXPERIENCE,
        description:
          DISCOVERED_TRANSLATION_KEYS.TAILOR_YOUR_AUDIO_WITH_CUSTOMIZABLE_VOICES_AND_SPEEDS,
      },
      {
        title: DISCOVERED_TRANSLATION_KEYS.STAY_AHEAD_WITH_WEEKLY_UPDATES,
        description:
          DISCOVERED_TRANSLATION_KEYS.BENEFIT_FROM_CONTINUOUS_INNOVATION_AND_ACCESS_TO_THE_LATEST_FEATURES,
      },
    ],
    []
  )

  const handleCheckout = async () => {
    setIsLoading(true)
    try {
      const { url } = await stripeControllerNewCheckout({
        period: selectedPeriod,
      })
      if (!url) return
      window.location.href = url
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={modalStyles.backdrop} onClick={handleClose}>
      <div className={styles.wrapper}>
        <div className={styles.card} onClick={e => e.stopPropagation()}>
          <div className={styles.header}>
            <div className={styles.logoWrap}>
              <SvgLogo className={styles.logo} />
            </div>
            <h2 className={styles.title}>
              {t(DISCOVERED_TRANSLATION_KEYS.GET_FRATECA_PRO)}
            </h2>
            <p className={styles.subtitle}>
              {t(
                DISCOVERED_TRANSLATION_KEYS.LISTEN_TO_UNLIMITED_PDFS_PAPERS_WEBSITES_BOOKS
              )}
            </p>
          </div>

          <div className={styles.featuresCard}>
            {features.map(f => (
              <div key={f.title.key} className={styles.featureItem}>
                <div className={styles.featureIcon}>
                  <Check size={18} />
                </div>
                <div className={styles.featureText}>
                  <div className={styles.featureTitle}>{t(f.title)}</div>
                  <div className={styles.featureDesc}>{t(f.description)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.planGrid}>
            <button
              type="button"
              className={cn(styles.planOption, {
                [styles.planOption_selected]:
                  selectedPeriod === NewCheckoutSessionDtoReqPeriod.YEAR,
              })}
              aria-pressed={
                selectedPeriod === NewCheckoutSessionDtoReqPeriod.YEAR
              }
              onClick={() =>
                setSelectedPeriod(NewCheckoutSessionDtoReqPeriod.YEAR)
              }
            >
              <div className={styles.planBadge}>
                {t(DISCOVERED_TRANSLATION_KEYS.PRO_DISCOUNT)}
              </div>
              <div className={styles.planTop}>
                <div className={styles.planName}>
                  {t(DISCOVERED_TRANSLATION_KEYS.YEARLY)}
                </div>
                <div className={styles.planCheck}>
                  <CheckCircle2 size={22} />
                </div>
              </div>
              <div className={styles.planPrice}>
                {t(DISCOVERED_TRANSLATION_KEYS.YEARLY_MONTHLY_PRICE)}
              </div>
              <div className={styles.planNote}>
                {t(DISCOVERED_TRANSLATION_KEYS.YEARLY_BILLING_NOTE)}
              </div>
            </button>

            <button
              type="button"
              className={cn(styles.planOption, {
                [styles.planOption_selected]:
                  selectedPeriod === NewCheckoutSessionDtoReqPeriod.MONTH,
              })}
              aria-pressed={
                selectedPeriod === NewCheckoutSessionDtoReqPeriod.MONTH
              }
              onClick={() =>
                setSelectedPeriod(NewCheckoutSessionDtoReqPeriod.MONTH)
              }
            >
              <div className={styles.planTop}>
                <div className={styles.planName}>
                  {t(DISCOVERED_TRANSLATION_KEYS.MONTHLY)}
                </div>
                <div className={styles.planCheck}>
                  <CheckCircle2 size={22} />
                </div>
              </div>
              <div className={styles.planPrice}>
                {t(DISCOVERED_TRANSLATION_KEYS.MONTHLY_PRICE)}
              </div>
              <div className={styles.planNote}>
                {t(DISCOVERED_TRANSLATION_KEYS.MONTHLY_BILLING_NOTE)}
              </div>
            </button>
          </div>

          <Button
            size="lg"
            className={styles.cta}
            onClick={handleCheckout}
            isLoading={isLoading}
          >
            {t(
              trialDays > 0
                ? DISCOVERED_TRANSLATION_KEYS.TRY_PREMIUM_FOR_FREE_FOR3_DAYS
                : DISCOVERED_TRANSLATION_KEYS.UPGRADE_TO_PREMIUM
            )}
          </Button>

          <div className={styles.footerLinks}>
            <Button
              onClick={() => window.open(RESTORE_PURCHASES_URL, "_blank")}
              variant="link"
              className={styles.footerLink}
              type="button"
            >
              {t(DISCOVERED_TRANSLATION_KEYS.RESTORE_PURCHASES)}
            </Button>
            <Button
              onClick={() => window.open(TERMS_OF_SERVICE_URL, "_blank")}
              variant="link"
              className={styles.footerLink}
              type="button"
            >
              {t(DISCOVERED_TRANSLATION_KEYS.TERMS)}
            </Button>
            <Button
              onClick={() => window.open(PRIVACY_POLICY_URL, "_blank")}
              variant="link"
              className={styles.footerLink}
              type="button"
            >
              {t(DISCOVERED_TRANSLATION_KEYS.PRIVACY)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
